import Phaser from 'phaser';
import { DPR } from '@constants/common.const';
import { PrizePopup } from './PrizePopup';

export interface PopupStackConfig {
  scene: Phaser.Scene;
  x?: number;
  y?: number;
  /** Gap between stacked popups in px (before DPR). Default: 8. */
  gap?: number;
}

export interface PopupStackEntryConfig {
  text: string;
  imagePath?: string;
  /** How long the popup stays visible before fading out (ms). Default: 1000. */
  duration?: number;
}

class PopupEntry {
  constructor(
    readonly popup: PrizePopup,
    private readonly onRemove: (e: PopupEntry) => void,
  ) {}

  remove(): void {
    this.onRemove(this);
  }
}

export class PopupStack {
  private readonly scene: Phaser.Scene;
  private readonly anchorX: number;
  private readonly anchorY: number;
  private readonly gap: number;

  private readonly entries: PopupEntry[] = [];
  private readonly moveTweens = new Map<PopupEntry, Phaser.Tweens.Tween>();

  constructor(config: PopupStackConfig) {
    this.scene = config.scene;
    this.anchorX = config.x ?? config.scene.scale.width / 2;
    this.anchorY = config.y ?? config.scene.scale.height * 0.3;
    this.gap = config.gap ?? 8;
  }

  push(config: PopupStackEntryConfig): void {
    const popup = new PrizePopup({
      scene: this.scene,
      x: this.anchorX,
      y: this.anchorY,
      text: config.text,
      imagePath: config.imagePath,
      onHide: () => this.removeEntry(entry),
    });

    // entry is captured by onHide above; assigned before onHide can fire
    const entry = new PopupEntry(popup, (e) => this.removeEntry(e));
    this.entries.push(entry);
    this.reflow(true);

    popup.show(config.duration);
  }

  destroy(): void {
    this.moveTweens.forEach((t) => t.stop());
    this.moveTweens.clear();
    for (const entry of this.entries) {
      if (entry.popup.active) entry.popup.destroy();
    }
    this.entries.length = 0;
  }

  private removeEntry(entry: PopupEntry): void {
    const idx = this.entries.indexOf(entry);
    if (idx === -1) return;
    this.cancelMoveTween(entry);
    this.entries.splice(idx, 1);
  }

  private cancelMoveTween(entry: PopupEntry): void {
    const tween = this.moveTweens.get(entry);
    if (tween) {
      tween.stop();
      this.moveTweens.delete(entry);
    }
  }

  /**
   * Stack layout (bottom-to-top, index 0 = oldest = highest):
   *   entries[last] = newest, caret tip at anchorY
   *   entries[0]    = oldest, farthest above anchor
   */
  private reflow(animate: boolean): void {
    if (this.entries.length === 0) return;

    const gapPx = this.gap * DPR;
    let currentAnchorY = this.anchorY;

    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i];
      const h = entry.popup.height;
      const centerY = currentAnchorY - h / 2;

      if (animate) {
        this.cancelMoveTween(entry);
        const tween = this.scene.tweens.add({
          targets: entry.popup,
          y: centerY,
          duration: 200,
          ease: 'Cubic.easeOut',
          onComplete: () => this.moveTweens.delete(entry),
        });
        this.moveTweens.set(entry, tween);
      } else {
        entry.popup.setPosition(this.anchorX, centerY);
      }

      currentAnchorY = centerY - h / 2 - gapPx;
    }
  }
}
